import { createStyles, makeStyles, Paper } from "@material-ui/core";
import { RewardsDetails } from "core/src/domain/rewardDetails";
import * as React from "react";

import {
    baseColors,
    BuildingIcon,
    CalendarIcon,
    domain,
    EarningsIcon,
    getUserFromStorage,
    HirePromotionBoostIcon,
    HirePromotionCircleIcon,
    JobIcon,
    JobPriorityLabel,
    JobPriorityLegend,
    LeadMenuIcon,
    Link,
    MapMarkerIcon,
    RelodeTheme,
    ReportCardIcon,
    Text,
} from "../..";

const useStyles = makeStyles(
    (theme: RelodeTheme) =>
        createStyles({
            paper: {
                padding: theme.spacing(2),
            },
            header: {
                marginBottom: 15,
            },
            rewardsHeader: {
                textAlign: "center",
                lineHeight: "initial",
            },
            rewardsTop: {
                fontSize: 14,
            },
            rewardsAmount: {
                fontSize: 40,
                lineHeight: "40px",
                cursor: "pointer",
                fontWeight: "bold",
                fontStyle: "italic",
                fontFamily: "'hkgrotesk_bolditalic',Helvetica,sans-serif",
                color: baseColors.purple,
            },
            rewardsAmountText: {
                fontSize: 40,
                lineHeight: "40px",
                fontWeight: "bolder",
                fontStyle: "italic",
                textDecoration: "unset",
                color: baseColors.purple,
            },
            jobDetails: {
                display: "flex",
                flexDirection: "column",
                marginTop: 30,
            },
            jobDetailsItem: {
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: 8,
                padding: theme.spacing(0.5, 0),
            },
            jobDetailsIcon: {
                display: "flex",
                alignItems: "center",
                color: theme.relode.colors.pagePrimaryColor,
            },
            hirePromotionContainer: {
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                margin: theme.spacing(1, 0, -1, 0),
                width: "100%",
            },
            hirePromotionIconContainer: {
                margin: "0 auto",
                width: "100%",
            },
            hirePromotionIconLabel: {
                display: "inline-flex",
                columnGap: "6px",
                alignItems: "center",
            },
            hirePromotionActive: {
                border: `1px solid ${theme.relode.colors.backgroundHighlightAlt.main}`,
                borderRadius: "3px",
                backgroundColor: theme.relode.colors.pageSecondaryBackground,
                color: theme.relode.colors.backgroundHighlightAlt.main,
                marginTop: "-15px",
            },
            hirePromotionActiveInnerTop: {
                backgroundColor:
                    theme.relode.colors.backgroundHighlightAlt.main,
                color: "white",
                fontSize: 14,
                fontWeight: 700,
                display: "flex",
                flexDirection: "row",
                gap: 25,
                justifyContent: "space-between",
                alignItems: "center",
                padding: theme.spacing(1, 3),
                maxHeight: "50px",
            },
            hirePromotionActivePlusIcon: {
                backgroundColor: "white",
                width: "30px",
                height: "30px",
                color: theme.relode.colors.backgroundText.dark,
                borderRadius: "30px",
                position: "relative",
                margin: "0 auto",
                textAlign: "center",
                paddingTop: "2px",
            },
            hirePromotionActiveInnerBottom: {
                textAlign: "center",
                padding: theme.spacing(2, 0),
            },
            hirePromotionExpiredContainer: {
                justifyContent: "center",
                alignItems: "center",
                margin: theme.spacing(1, 0, -1, 0),
                width: "100%",
            },
            hirePromotionExpired: {
                border: `1px solid ${theme.relode.colors.backgroundHighlightAlt.main}`,
                borderRadius: "3px",
                backgroundColor: theme.relode.colors.pageSecondaryBackground,
                color: theme.relode.colors.backgroundHighlightAlt.main,
                textAlign: "center",
                padding: theme.spacing(1, 3),
                fontSize: 16,
                fontWeight: 400,
            },
        }),
    { name: "RewardsView" }
);

interface RewardsViewProps {
    altTitle?: string;
    companyName: string;
    openPositions: number;
    jobLocationName: string;
    jobSalaryInUSDollars: string;
    jobSalaryTypeName?: string;
    jobTypeName: string;
    jobPriorityTypeId?: number;
    hoursPerWeek: number;
    contractStartDate?: string;
    contractEndDate?: string;
    totalCommissionInUSDollars?: string;
    rewardsDetails?: RewardsDetails;
    actionableLinks: boolean;
    openModal?: (e: React.MouseEvent) => void;
}

export const RewardsView = (props: RewardsViewProps) => {
    const {
        altTitle,
        companyName,
        openPositions,
        jobLocationName,
        jobTypeName,
        jobSalaryInUSDollars,
        jobSalaryTypeName,
        jobPriorityTypeId,
        hoursPerWeek,
        contractStartDate,
        contractEndDate,
        totalCommissionInUSDollars,
        rewardsDetails,
        actionableLinks,
        openModal,
    } = props;
    const classes = useStyles({});
    const [href, setHref] = React.useState("");
    const [title, setTitle] = React.useState("");
    const user = getUserFromStorage();
    const isClient =
        user.role === domain.UserRole.Client ||
        user.role === domain.UserRole.ClientAdmin;

    React.useEffect(() => {
        const fullAddress = `${jobLocationName}`;
        setHref(
            `https://www.google.com/maps/search/?api=1&query=${encodeURI(
                fullAddress.trim()
            )}`
        );
    }, [jobLocationName]);

    React.useEffect(() => {
        if (altTitle) {
            setTitle(altTitle);
        }
    }, [altTitle]);

    const jobLocationIsNotRemote = jobLocationName !== "Remote";

    return (
        <Paper elevation={isClient ? 0 : 1} className={classes.paper}>
            {altTitle ? (
                <Text variant="h2">
                    {title}
                </Text>
            ) : null}
            {actionableLinks ? (
                <div className={classes.rewardsHeader}>
                    <Text className={classes.rewardsTop}>rewards up to</Text>
                    <Link
                        id={"rewardsAmount-link"}
                        className={classes.rewardsAmount}
                        onClick={openModal}
                    >
                        {totalCommissionInUSDollars}
                    </Link>
                </div>
            ) : (
                <div className={classes.rewardsHeader}>
                    {isClient ? "" : <Text className={classes.rewardsTop}>rewards up to</Text> }
                    <Text className={classes.rewardsAmountText}>
                        {totalCommissionInUSDollars}
                    </Text>
                </div>
            )}
            {rewardsDetails?.isPromotionActive ? (
                <div className={classes.hirePromotionContainer}>
                    <div className={classes.hirePromotionIconContainer}>
                        <div className={classes.hirePromotionActivePlusIcon}>
                            <HirePromotionCircleIcon />
                        </div>

                        <div className={classes.hirePromotionActive}>
                            <div
                                className={classes.hirePromotionActiveInnerTop}
                            >
                                <div className={classes.hirePromotionIconLabel}>
                                    <HirePromotionBoostIcon />
                                    hire promotion
                                </div>
                                <div>
                                    {rewardsDetails.hirePromotionDescription}
                                </div>
                            </div>
                            <div
                                className={
                                    classes.hirePromotionActiveInnerBottom
                                }
                            >
                                <Text className={classes.rewardsAmount}>
                                    {
                                        rewardsDetails.totalPromotionCommissionInUSDollars
                                    }
                                </Text>
                            </div>
                        </div>
                    </div>
                </div>
            ) : rewardsDetails?.hirePromotionDescription?.length > 0 ? (
                <div className={classes.hirePromotionExpiredContainer}>
                    <div className={classes.hirePromotionExpired}>
                        {rewardsDetails.hirePromotionDescription}
                    </div>
                </div>
            ) : null}
            <div className={classes.jobDetails}>
                <div className={classes.jobDetailsItem}>
                    <div className={classes.jobDetailsIcon}>
                        <BuildingIcon />
                    </div>
                    <Text variant="body1" dataCy="rewardsView-companyName-text">
                        {companyName}
                    </Text>
                </div>
                <div className={classes.jobDetailsItem}>
                    <div className={classes.jobDetailsIcon}>
                        <MapMarkerIcon />
                    </div>
                    <Text
                        variant="body1"
                        dataCy="rewardsView-jobLocationName-text"
                    >
                        {jobLocationName}
                        {"  "}
                    </Text>
                    {actionableLinks && jobLocationIsNotRemote ? (
                        <Link
                            id="rewardsView-mapLink"
                            href={href}
                            highlightType="external"
                            target="_blank"
                        >
                            <Text
                                variant="body1"
                                dataCy="rewardsView-mapLink-text"
                            >
                                map
                            </Text>
                        </Link>
                    ) : null}
                </div>
                <div className={classes.jobDetailsItem}>
                    <div className={classes.jobDetailsIcon}>
                        <JobIcon />
                    </div>
                    <Text
                        variant="body1"
                        dataCy="rewardsView-jobTypeAndHoursPerWeek-text"
                    >
                        {`${jobTypeName} (${hoursPerWeek} hrs/week)`}
                    </Text>
                </div>
                {contractStartDate && contractEndDate ? (
                    <div className={classes.jobDetailsItem}>
                        <div className={classes.jobDetailsIcon}>
                            <CalendarIcon />
                        </div>
                        <Text
                            variant="body1"
                            dataCy="rewardsView-contractStartAndEndDate-text"
                        >
                            {`${contractStartDate} - ${contractEndDate}`}
                        </Text>
                    </div>
                ) : null}
                {jobSalaryInUSDollars ? (
                    <div className={classes.jobDetailsItem}>
                        <div className={classes.jobDetailsIcon}>
                            <EarningsIcon />
                        </div>
                        <Text
                            variant="body1"
                            dataCy="rewardsView-jobSalaryType-text"
                        >
                            {jobSalaryInUSDollars}{" "}
                            {jobSalaryTypeName
                                ? jobSalaryTypeName.toLowerCase()
                                : null}
                        </Text>
                    </div>
                ) : null}
                <div className={classes.jobDetailsItem}>
                    <div className={classes.jobDetailsIcon}>
                        <LeadMenuIcon />
                    </div>
                    <Text
                        variant="body1"
                        dataCy="rewardsView-openPositions-text"
                    >{`${openPositions} openings`}</Text>
                </div>
                {jobPriorityTypeId !== null ? (
                    <>
                        <div className={classes.jobDetailsItem}>
                            <div className={classes.jobDetailsIcon}>
                                <ReportCardIcon />
                            </div>
                            <JobPriorityLegend withGradeIcon={true}>
                                <JobPriorityLabel
                                    jobPriority={jobPriorityTypeId}
                                />
                            </JobPriorityLegend>
                        </div>
                    </>
                ) : null}
            </div>
        </Paper>
    );
};
